/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Speaker Dialogue Studio & Web Audio Mixer View (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Download,
  CheckCircle2,
  Clock,
  TrendingDown,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useTranslation } from '../i18n';
import { useAudioMixerStore } from '../stores/useAudioMixerStore';
import { MultiSpeakerDialogEditor } from '../components/mixer/MultiSpeakerDialogEditor';
import { WebAudioTimelineMixer } from '../components/mixer/WebAudioTimelineMixer';
import { MixerTransportControls } from '../components/mixer/MixerTransportControls';
import { MixerExportModal } from '../components/mixer/MixerExportModal';

export const AudioMixerStudioView: React.FC = () => {
  const { t } = useTranslation();
  const {
    projectTitle,
    activeStudioTab,
    speakers,
    dialogueBlocks,
    totalDuration,
    tracks,
    duckingSettings,
    bgmPresets,
    selectedBgmId,
    setProjectTitle,
    setActiveStudioTab,
    syncScriptToTimeline,
    selectBgmPreset,
    getEstimatedCredits,
    getTotalWordCount,
  } = useAudioMixerStore();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  const estimatedCredits = getEstimatedCredits();
  const totalWords = getTotalWordCount();

  const handleSavedToGallery = () => {
    setSuccessToast(t('mixer.savedToGalleryNotice'));
    setTimeout(() => {
      setSuccessToast(null);
    }, 3000);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Studio Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1.5 flex-1">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <SophiaIcon name="voice-recognition" size={18} className="text-primary shrink-0" />
            </div>
            <input
              type="text"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              className="text-lg font-bold font-heading text-foreground bg-transparent border-b border-transparent hover:border-surface-border focus:border-primary focus:outline-none transition-colors px-1"
            />
          </div>
          <p className="text-xs text-foreground-muted">
            {t('mixer.studioSubtitle')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              syncScriptToTimeline();
              setActiveStudioTab('timeline');
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-surface-elevated text-foreground hover:bg-surface-border border border-surface-border text-xs font-semibold transition-colors"
          >
            <SophiaIcon name="algorithm" size={14} className="text-primary shrink-0" />
            <span>{t('mixer.syncTimelineBtn')}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsExportModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-sm transition-colors"
          >
            <Download className="size-3.5" />
            <span>{t('mixer.exportMixBtn')}</span>
          </button>
        </div>
      </div>

      {/* Success Notification Alert */}
      {successToast && (
        <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-medium animate-slide-up">
          <CheckCircle2 className="size-4 shrink-0" />
          <span>{successToast}</span>
        </div>
      )}

      {/* Metrics Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <SophiaIcon name="voice-recognition" size={18} className="text-primary shrink-0" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {speakers.length}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('mixer.metricSpeakers')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
            <Clock className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {totalDuration.toFixed(1)}s
            </div>
            <div className="text-[11px] text-foreground-muted">{t('mixer.metricDuration')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
            <TrendingDown className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {duckingSettings.enabled ? `${duckingSettings.duckingPercent}%` : t('mixer.disabledLabel')}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('mixer.smartDuckingStatus')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <SophiaIcon name="battery" size={18} className="text-primary shrink-0" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              ~{estimatedCredits} cr
            </div>
            <div className="text-[11px] text-foreground-muted">
              {totalWords} {t('cat.wordsCountLabel')}
            </div>
          </div>
        </div>
      </div>

      {/* Main Studio Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-surface-border pb-1">
        <button
          type="button"
          onClick={() => setActiveStudioTab('script')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-b-2 ${
            activeStudioTab === 'script'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-foreground-muted hover:text-foreground'
          }`}
        >
          <SophiaIcon name="brainstorm" size={15} className="shrink-0" />
          <span>{t('mixer.tabScriptEditor')}</span>
          <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-surface-elevated font-mono">
            {dialogueBlocks.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            syncScriptToTimeline();
            setActiveStudioTab('timeline');
          }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-b-2 ${
            activeStudioTab === 'timeline'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-foreground-muted hover:text-foreground'
          }`}
        >
          <SophiaIcon name="voice-recognition" size={15} className="shrink-0" />
          <span>{t('mixer.tabTimelineMixer')}</span>
          <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-surface-elevated font-mono">
            {tracks.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveStudioTab('export')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-b-2 ${
            activeStudioTab === 'export'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-foreground-muted hover:text-foreground'
          }`}
        >
          <SophiaIcon name="solution" size={15} className="shrink-0" />
          <span>{t('mixer.tabBgmAndExport')}</span>
        </button>
      </div>

      {/* Tab Content 1: Multi-Speaker Script Editor */}
      {activeStudioTab === 'script' && <MultiSpeakerDialogEditor />}

      {/* Tab Content 2: Multi-Track Timeline DAW Mixer */}
      {activeStudioTab === 'timeline' && (
        <div className="space-y-4">
          <MixerTransportControls />
          <WebAudioTimelineMixer onOpenExport={() => setIsExportModalOpen(true)} />
        </div>
      )}

      {/* Tab Content 3: Background Music Library & Master Export */}
      {activeStudioTab === 'export' && (
        <div className="space-y-6">
          {/* BGM Selector Section */}
          <div className="p-5 rounded-2xl bg-surface border border-surface-border space-y-4 shadow-xs">
            <div className="flex items-center gap-2">
              <SophiaIcon name="solution" size={20} className="text-amber-500" />
              <div>
                <h3 className="text-sm font-bold text-foreground">
                  {t('mixer.bgmLibraryTitle')}
                </h3>
                <p className="text-xs text-foreground-muted">
                  {t('mixer.bgmLibrarySubtitle')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {bgmPresets.map((bgm) => {
                const isSelected = selectedBgmId === bgm.id;
                return (
                  <div
                    key={bgm.id}
                    onClick={() => selectBgmPreset(bgm.id)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all flex flex-col justify-between space-y-3 ${
                      isSelected
                        ? 'bg-amber-500/10 border-amber-500 ring-1 ring-amber-500/30 shadow-xs'
                        : 'bg-surface-elevated border-surface-border hover:border-surface-border/80'
                    }`}
                  >
                    <div>
                      <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">
                        {bgm.genre}
                      </span>
                      <h4 className="text-xs font-bold text-foreground">
                        {bgm.title}
                      </h4>
                    </div>

                    <div className="flex items-center justify-between text-[10px] font-mono text-foreground-muted pt-2 border-t border-surface-border">
                      <span>{bgm.durationSec}s</span>
                      <span>{bgm.bpm} BPM</span>
                      <span className="text-amber-500 font-bold">
                        {isSelected ? t('mixer.bgmActive') : t('mixer.selectBgm')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Master Export Trigger Card */}
          <div className="p-6 rounded-2xl bg-surface border border-surface-border flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-foreground">
                {t('mixer.readyToRenderTitle')}
              </h3>
              <p className="text-xs text-foreground-muted max-w-xl">
                {t('mixer.readyToRenderDesc')}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsExportModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-sm transition-colors shrink-0"
            >
              <Download className="size-4" />
              <span>{t('mixer.openExportModalBtn')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <MixerExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSaveToGallery={handleSavedToGallery}
      />
    </div>
  );
};
